import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	BroadcastService,
	BroadcastEventModel,
	SignalRState,
	ORDER_EVENT_TYPE
} from '@cv-invision/core';
import { Store } from '@ngxs/store';
import { RuleService } from '../service/rule.service';
import { RulesState } from '../state/rules.state';
import { RULES_INDICATOR } from '../model/constants';
import { SetIsRunningRules, UpdatedRuleOrders, SetRuleSet } from '../state/action/rules.actions';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const RULE_SET_EVENT = 'ruleSetEvent';

@Component({
	selector: 'cv-rule-set',
	templateUrl: './rule-set.component.html',
	styleUrls: ['./rule-set.component.scss']
})
export class RuleSetComponent implements OnInit, OnDestroy {
	isOpen: boolean;
	orderData: any;
	ruleSet: any[];
	currentRulesSet: any[];
	executedRule: string[];
	deferredRuleIndicator: string;
	noRuleSetAvailable = false;
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);
	private atComponentDestroy = new Subject();

	constructor(
		private broadcastService: BroadcastService,
		private store: Store,
		private ruleService: RuleService
	) {
		this.isOpen = false;
		this.ruleSet = [];
	}

	ngOnInit(): void {
		this.registerRuleSetListener();
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * close modal windows
	 */
	close(): void {
		this.isOpen = false;
		this.currentRulesSet = [];
		this.executedRule = [];
		this.noRuleSetAvailable = false;
	}

	/**
	 * run set of rule
	 */
	reRunRules(): void {
		const { id, documentId } = this.orderData;
		this.isOpen = false;
		this.store.dispatch(new SetIsRunningRules(true));
		this.ruleService
			.forceExecutionRuleSets(id, documentId, this.executedRule.join(','))
			.subscribe(response => {
				const isRunningRules = response.deferredRuleIndicator === RULES_INDICATOR.INPROCESS;
				this.store.dispatch(new SetRuleSet(id));
				this.store.dispatch(new UpdatedRuleOrders(response, this.orderData));
				if (!isRunningRules) {
					this.store.dispatch(new SetIsRunningRules(isRunningRules));
				}
			});

		this.channels$.pipe(takeUntil(this.atComponentDestroy)).subscribe((channels: any) => {
			if (channels && channels[`orders:${id}`]) {
				this.onRulesEvents(channels[`orders:${id}`].events);
			}
		});
	}

	/**
	 * get the executed rulesset  after the re-run finished
	 * @param channels
	 */
	private onRulesEvents(channels: Array<any>): void {
		const isForcedRules = channels.find(
			item => item.event === ORDER_EVENT_TYPE.ORDER_RULES_FORCE_EXECUTION_CI
		);
		if (isForcedRules) {
			setTimeout(() => {
				this.getRulesSet();
			}, 1500);
		}
	}

	/**
	 * set rule set available for the orders
	 */
	async getRulesSet(): Promise<void> {
		await this.ruleService.getAvailableRuleSets(this.orderData.id).toPromise();

		this.currentRulesSet = this.store.selectSnapshot(RulesState.getRuleSet);
		this.ruleSet = this.store.selectSnapshot(RulesState.getAvailableRuleSet);
		if (this.ruleSet.length) {
			const r = this.ruleSet.filter(f => this.currentRulesSet.includes(f.id));
			if (r.length) {
				r.forEach(rl => {
					this.executedRule.push(rl.id);
				});
				this.noRuleSetAvailable = false;
			}
		} else {
			this.noRuleSetAvailable = true;
		}
	}

	/**
	 *	set if a checkbox is by default checked
	 */
	isCheked(id: string): boolean {
		return this.currentRulesSet.includes(id);
	}

	/**
	 * get input id for selected rule-set
	 * @param item
	 */
	onCheckChanges(e: any, id: string): void {
		this.setExecuteIdRule(id);
	}

	/**
	 * set execute value id
	 * @param id
	 */
	private setExecuteIdRule(id: string): void {
		const chcekR = this.executedRule.find(r => r === id);
		if (chcekR) {
			this.executedRule = this.executedRule.filter(r => r !== id);
		} else {
			this.executedRule = this.executedRule.concat(id);
		}
	}

	/**
	 * Register Lister to Show/Hide the Rules Set
	 */
	private registerRuleSetListener(): void {
		this.broadcastService.onEvent(RULE_SET_EVENT)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((broadcast: BroadcastEventModel) => {
				this.currentRulesSet = [];
				this.executedRule = [];
				this.noRuleSetAvailable = false;
				this.isOpen = broadcast.event.isOpen;
				this.orderData = broadcast.event.order;
				this.getRulesSet();
			});
	}
}
